import { useEffect, useRef, type ReactNode } from 'react'

import { cn } from '../lib/ui'

/**
 * A centred dialog. Escape and a backdrop click close it, focus moves inside on
 * open and returns to whatever opened it on close, and Tab is kept inside the
 * panel so a keyboard user cannot land on the board behind it.
 */
export function Modal({
  open,
  onClose,
  title,
  titleIcon,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  /**
   * Optional mark shown beside the heading.
   *
   * Separate from `title` on purpose: the title doubles as the dialog's
   * aria-label, so widening it to a ReactNode would hand the accessible name an
   * object instead of a string. The icon is decorative and stays out of it.
   */
  titleIcon?: ReactNode
  description?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'md' | 'lg'
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const restoreRef = useRef<HTMLElement | null>(null)

  // The escape handler must always call the LATEST onClose, but onClose must
  // not be an effect dependency. Callers pass a plain arrow function, so its
  // identity changes on every parent render — and this effect moves focus to
  // the first control in the panel. With onClose in the deps, typing one
  // character into a field re-ran the whole setup and threw focus back to the
  // first input, which made every textarea in every dialog unusable.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    restoreRef.current = document.activeElement as HTMLElement | null

    const panel = panelRef.current
    const focusable = panel?.querySelector<HTMLElement>(
      'input, textarea, select, button, [tabindex]:not([tabindex="-1"])',
    )
    focusable?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab' || !panel) return
      const targets = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null)
      if (targets.length === 0) return
      const first = targets[0]!
      const last = targets[targets.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      restoreRef.current?.focus?.()
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/40 p-4 pt-[8vh] backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'w-full rounded-card border border-ink-200 bg-white shadow-xl shadow-ink-950/10',
          'dark:border-ink-800 dark:bg-ink-900 dark:shadow-black/50',
          size === 'lg' ? 'max-w-3xl' : 'max-w-xl',
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-ink-200 px-5 py-3.5 dark:border-ink-800">
          <div>
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-ink-900 dark:text-ink-100">
              {titleIcon}
              {title}
            </h2>
            {description && <p className="mt-0.5 text-xs text-ink-500">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-m-1 rounded-md p-1 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700 dark:hover:bg-ink-800 dark:hover:text-ink-200"
          >
            <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden="true">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="px-5 py-4">{children}</div>

        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-ink-200 px-5 py-3 dark:border-ink-800">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}
